(() => {
  'use strict';
  /* Original Liquid-friendly editorial interactions. Visual references:
     reactbits.dev/components/accordion-gallery
     reactbits.dev/text-animations/scroll-velocity
     reactbits.dev/text-animations/scroll-reveal */
  window.VarelixEditorial2?.destroy();
  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const wide = matchMedia('(min-width: 990px)');
  const desktop = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const query = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const mod = (value, length) => ((value % length) + length) % length;
  const allowed = () => !reduced.matches && root.dataset.vx2Motion !== 'off' && !window.Shopify?.designMode;
  let disposers = [], globalDisposers = [], queued = false, destroyed = false;
  const pageWrapper = document.querySelector('.page-wrapper');
  const scrollSource = () => wide.matches && pageWrapper ? pageWrapper : window;
  const scrollPosition = () => scrollSource() === window ? window.scrollY : pageWrapper.scrollTop;
  let scrollY = scrollPosition(), scrollAt = performance.now(), impulseAt = 0, impulse = 0, direction = 1;
  function on(target, event, handler, options, global = false) {
    target.addEventListener(event, handler, options);
    (global ? globalDisposers : disposers).push(() => target.removeEventListener(event, handler, options));
  }
  function observe(el, callback) {
    if (!('IntersectionObserver' in window)) { callback(true); return; }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => callback(entry.isIntersecting)), { threshold: .01 });
    observer.observe(el); disposers.push(() => observer.disconnect());
  }
  function setupGallery(grid) {
    const cards = query(':scope > .vxh-space', grid);
    if (!cards.length) return;
    grid.classList.add('vxe-gallery');
    let selected = Math.floor(cards.length / 2), pointerCard = -1, visible = false;
    const focusIndex = () => cards.findIndex(card => card.contains(document.activeElement));
    function select(index) {
      selected = clamp(index, 0, cards.length - 1);
      cards.forEach((card, i) => card.dataset.vxeActive = String(i === selected));
    }
    function restoreSelection() {
      const focused = focusIndex();
      select(focused >= 0 ? focused : pointerCard >= 0 ? pointerCard : Math.floor(cards.length / 2));
    }
    cards.forEach((card, index) => {
      const link = card.querySelector(':scope > a');
      const media = card.querySelector('.vxh-space-media');
      if (!link || !media) return;
      // Wrap existing copy once; its wording, link, and accessible name are preserved.
      let caption = link.querySelector(':scope > .vxe-caption');
      if (!caption) {
        caption = document.createElement('div'); caption.className = 'vxe-caption';
        [...link.children].filter(child => child !== media).forEach(child => caption.append(child));
        link.append(caption);
      }
      if (!link.querySelector(':scope > .vxe-index')) {
        const number = document.createElement('span'); number.className = 'vxe-index';
        number.textContent = String(index + 1).padStart(2, '0'); number.setAttribute('aria-hidden', 'true'); link.append(number);
      }
      let frame = 0, x = 0, y = 0;
      const reset = () => {
        cancelAnimationFrame(frame); frame = 0;
        card.style.setProperty('--vxe-photo-x', '0px'); card.style.setProperty('--vxe-photo-y', '0px');
        card.style.setProperty('--vxe-caption-x', '0px'); card.style.setProperty('--vxe-caption-y', '0px');
      };
      on(card, 'pointerenter', event => {
        if (!desktop.matches || event.pointerType === 'touch') return;
        pointerCard = index; select(index);
      });
      on(card, 'pointermove', event => {
        if (!desktop.matches || !allowed() || !visible || document.hidden || event.pointerType === 'touch') return;
        const rect = card.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        x = clamp((event.clientX - rect.left) / rect.width - .5, -.5, .5);
        y = clamp((event.clientY - rect.top) / rect.height - .5, -.5, .5);
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          card.style.setProperty('--vxe-photo-x', `${(-x * 28).toFixed(2)}px`);
          card.style.setProperty('--vxe-photo-y', `${(-y * 20).toFixed(2)}px`);
          card.style.setProperty('--vxe-caption-x', `${(x * 7).toFixed(2)}px`);
          card.style.setProperty('--vxe-caption-y', `${(y * 5).toFixed(2)}px`);
        });
      }, { passive: true });
      on(card, 'pointerleave', () => { if (pointerCard === index) pointerCard = -1; reset(); });
      on(link, 'focus', () => select(index));
      on(link, 'keydown', event => {
        if (!desktop.matches || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? cards.length - 1 : mod(index + (event.key === 'ArrowRight' ? 1 : -1), cards.length);
        const nextLink = cards[next].querySelector(':scope > a');
        if (nextLink) { event.preventDefault(); nextLink.focus({ preventScroll: true }); }
      });
      on(document, 'visibilitychange', () => { if (document.hidden) reset(); });
      disposers.push(reset);
    });
    select(selected);
    on(grid, 'pointerleave', () => { pointerCard = -1; restoreSelection(); });
    on(grid, 'focusout', () => queueMicrotask(() => { if (!destroyed && grid.isConnected) restoreSelection(); }));
    observe(grid, state => { visible = state; grid.dataset.vxeVisible = String(state); });
  }
  function setupRibbon(band) {
    const track = band.querySelector('.vx2-ribbon-track');
    const sets = query('.vx2-ribbon-set', band);
    if (!track || sets.length < 2) return;
    band.classList.add('vxe-velocity');
    // Screen readers encounter the sentence once; frame updates only touch CSS variables.
    sets.slice(1).forEach(set => set.setAttribute('aria-hidden', 'true'));
    const enabled = allowed(); band.dataset.vxeEnabled = String(enabled);
    let frame = 0, visible = false, hovered = false, focused = false, width = 0;
    let position = 0, last = 0, speed = desktop.matches ? 30 : 23;
    const stop = () => { cancelAnimationFrame(frame); frame = 0; last = 0; band.dataset.vxeRunning = 'false'; };
    function step(now) {
      frame = 0;
      if (!enabled || !visible || document.hidden || hovered || focused || !width) { stop(); return; }
      const dt = last ? Math.min((now - last) / 1000, .05) : 0; last = now;
      const base = desktop.matches ? 30 : 23;
      const remainingImpulse = impulse * Math.exp(-(now - impulseAt) / 650);
      const target = direction * (base + remainingImpulse);
      speed += (target - speed) * (1 - Math.exp(-dt * 4.5));
      position = mod(position + speed * dt, width);
      track.style.setProperty('--vxe-ribbon-x', `${(-position).toFixed(2)}px`);
      track.style.setProperty('--vxe-ribbon-skew', `${clamp(-speed * .007, -1.7, 1.7).toFixed(3)}deg`);
      band.dataset.vxeRunning = 'true'; frame = requestAnimationFrame(step);
    }
    function sync() {
      if (!enabled || !visible || document.hidden || hovered || focused || !width) { stop(); return; }
      if (!frame) { last = 0; frame = requestAnimationFrame(step); }
    }
    function measure() {
      const nextWidth = sets[0].offsetWidth;
      if (nextWidth > 0) { position = width ? position / width * nextWidth : 0; width = nextWidth; }
      sync();
    }
    if (enabled) {
      observe(band, state => { visible = state; sync(); });
      on(band, 'pointerenter', event => { if (event.pointerType !== 'touch') { hovered = true; sync(); } });
      on(band, 'pointerleave', () => { hovered = false; sync(); });
      on(band, 'focusin', () => { focused = true; sync(); });
      on(band, 'focusout', event => { focused = band.contains(event.relatedTarget); sync(); });
      on(document, 'visibilitychange', sync);
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(measure); observer.observe(sets[0]); disposers.push(() => observer.disconnect());
      } else { on(window, 'resize', measure, { passive: true }); }
      measure();
    } else { track.style.removeProperty('--vxe-ribbon-x'); track.style.removeProperty('--vxe-ribbon-skew'); }
    disposers.push(() => { stop(); track.style.removeProperty('--vxe-ribbon-x'); track.style.removeProperty('--vxe-ribbon-skew'); });
  }
  function init() {
    disposers.forEach(dispose => dispose()); disposers = [];
    scrollY = scrollPosition(); scrollAt = performance.now();
    query('.varelix-home .vxh-spaces-grid').forEach(setupGallery);
    query('.varelix-home .vx2-ribbon').forEach(setupRibbon);
  }
  function queueInit() {
    if (queued || destroyed) return; queued = true;
    queueMicrotask(() => { queued = false; if (!destroyed) init(); });
  }
  function trackScroll(event) {
    if (event.currentTarget !== scrollSource()) return;
    const now = performance.now(), y = scrollPosition(), delta = y - scrollY, elapsed = Math.max(16, now - scrollAt);
    if (Math.abs(delta) > .5 && !document.hidden && allowed()) {
      impulse = Math.min(desktop.matches ? 165 : 110, Math.abs(delta) / elapsed * 75);
      impulseAt = now; direction = delta > 0 ? 1 : -1;
    }
    scrollY = y; scrollAt = now;
  }
  on(window, 'scroll', trackScroll, { passive: true }, true);
  if (pageWrapper) on(pageWrapper, 'scroll', trackScroll, { passive: true }, true);
  on(wide, 'change', queueInit, undefined, true);
  on(reduced, 'change', queueInit, undefined, true); on(desktop, 'change', queueInit, undefined, true);
  on(document, 'shopify:section:load', queueInit, undefined, true);
  on(document, 'shopify:section:unload', queueInit, undefined, true);
  let lastMode = root.dataset.vx2Motion;
  const modeObserver = new MutationObserver(() => {
    if (lastMode !== root.dataset.vx2Motion) { lastMode = root.dataset.vx2Motion; queueInit(); }
  });
  modeObserver.observe(root, { attributes: true, attributeFilter: ['data-vx2-motion'] });
  globalDisposers.push(() => modeObserver.disconnect());
  window.VarelixEditorial2 = { destroy() { destroyed = true; disposers.forEach(fn => fn()); globalDisposers.forEach(fn => fn()); } };
  init();
})();
